import React, { useState } from 'react';

import { Page, LayoutSingleColumn, TrustBanner } from '../../components';
import SkeepersCarouselReviews from '../../components/SkeepersCarouselReviews/SkeepersCarouselReviews';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { submitContactForm } from '../../util/api';
import { trackRedditLead } from '../../util/redditPixelEvents';
import { openTidioChat } from '../../util/tidioChat';
import css from './ContactPage.module.css';

const CALENDAR_BOOKING_URL = 'https://calendar.app.google/shsPuuDMcMN3Cub89';

const ContactPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const onSubmit = async e => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setStatus(null);

    try {
      await submitContactForm({ name, email, subject, message });

      setStatus({ type: 'success', message: 'Merci ! Votre message a été envoyé.' });
      trackRedditLead();
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (err) {
      setStatus({
        type: 'error',
        message: "Désolé, une erreur s'est produite. Veuillez réessayer.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page
      title="Nous Contacter | Neokasa"
      description="Contactez l'équipe Neokasa pour toute question sur nos produits domotique et maison connectée."
      scrollingDisabled={false}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.container}>
            <h1 className={css.title}>Contactez-nous</h1>
            <p className={css.subtitle}>
              Vous avez une question sur les appareils domotiques ou vos commandes ? Envoyez-nous un
              message.
            </p>

            <div className={css.columns}>
              <div className={css.formColumn}>
                <h2 className={css.columnHeading}>Formulaire de contact</h2>
                <form className={css.form} onSubmit={onSubmit} noValidate>
                  <div className={css.fieldRow}>
                    <label className={css.label} htmlFor="name">
                      Nom
                    </label>
                    <input
                      id="name"
                      className={css.input}
                      type="text"
                      name="name"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      required
                      placeholder="Votre nom"
                    />
                  </div>

                  <div className={css.fieldRow}>
                    <label className={css.label} htmlFor="email">
                      Email
                    </label>
                    <input
                      id="email"
                      className={css.input}
                      type="email"
                      name="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      placeholder="vous@exemple.com"
                    />
                  </div>

                  <div className={css.fieldRow}>
                    <label className={css.label} htmlFor="subject">
                      Sujet
                    </label>
                    <input
                      id="subject"
                      className={css.input}
                      type="text"
                      name="subject"
                      value={subject}
                      onChange={e => setSubject(e.target.value)}
                      required
                      placeholder="Comment pouvons-nous vous aider ?"
                    />
                  </div>

                  <div className={css.fieldRow}>
                    <label className={css.label} htmlFor="message">
                      Message
                    </label>
                    <textarea
                      id="message"
                      className={css.textarea}
                      name="message"
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      required
                      placeholder="Écrivez votre message ici..."
                      rows={6}
                    />
                  </div>

                  {status ? (
                    <div className={status.type === 'success' ? css.success : css.error}>
                      {status.message}
                    </div>
                  ) : null}

                  <button className={css.submit} type="submit" disabled={submitting}>
                    {submitting ? 'Envoi en cours…' : 'Envoyer le message'}
                  </button>
                </form>
              </div>

              <aside className={css.contactColumn} aria-label="Coordonnées Neokasa">
                <h2 className={css.columnHeading}>Nous joindre</h2>
                <div className={css.contactCard}>
                  <div className={css.contactRow}>
                    <span className={css.contactLabel}>Téléphone</span>
                    <div className={css.phoneLine}>
                      <a className={css.contactLink} href="tel:+33189480612">
                        01&nbsp;89&nbsp;48&nbsp;06&nbsp;12
                      </a>
                      <span className={css.contactHours}>
                        Service client disponible du lundi au vendredi (hors jours fériés), de 09h à
                        18h. Appel gratuit.
                      </span>
                    </div>
                  </div>
                  <div className={css.contactRow}>
                    <span className={css.contactLabel}>Email</span>
                    <a className={css.contactLink} href="mailto:contact@neokasa.fr">
                      contact@neokasa.fr
                    </a>
                  </div>
                  <div className={css.contactRow}>
                    <span className={css.contactLabel}>Chat en direct</span>
                    <p className={css.contactHours}>
                      Chattez en direct avec nos agents disponibles du lundi au vendredi (hors jours
                      fériés), de 09h à 18h.
                    </p>
                    <button type="button" className={css.chatButton} onClick={openTidioChat}>
                      Ouvrir le chat
                    </button>
                  </div>
                </div>

                <div className={css.appointmentCard}>
                  <h3 className={css.appointmentTitle}>Prise de rendez-vous domotique</h3>
                  <p className={css.appointmentLead}>
                    Réservez un échange de 30 minutes en visioconférence avec un expert Neokasa
                    pour cadrer votre projet maison connectée.
                  </p>
                  <a
                    href={CALENDAR_BOOKING_URL}
                    className={css.appointmentButton}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Prendre rendez-vous (30 min)
                  </a>
                </div>
              </aside>
            </div>
          </div>

          <div className={css.trustBannerSpacer}>
            <SkeepersCarouselReviews
              className={css.verifiedReviewsCarousel}
              titleClassName={css.carouselSectionTitle}
            />
            <TrustBanner />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default ContactPage;
